import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLMetadata = () => {
    const metaTagSyntaxCode = `<meta name="property-name" content="value">
<meta property="property-name" content="value">
<meta http-equiv="property-name" content="value">`;

    const descriptionMetaCode = `<meta name="description" content="Learn HTML5 with our comprehensive tutorial. Master tags, semantics, forms, and modern web development practices.">`;

    const robotsMetaCode = `<!-- Allow indexing and link following (default) --&gt;
<meta name="robots" content="index, follow">

<!-- Prevent indexing (use for admin pages, duplicates) --&gt;
<meta name="robots" content="noindex, nofollow">

<!-- Index but don't follow links --&gt;
<meta name="robots" content="index, nofollow">

<!-- Advanced directives --&gt;
<meta name="robots" content="index, follow, max-snippet:100, max-image-preview:large">`;

    const canonicalUrlCode = `<link rel="canonical" href="https://example.com/html-tutorial">`;
    const canonicalUrlExampleCode = `<link rel="canonical" href="https://example.com/products/widget">`;

    const openGraphCode = `<!-- Essential Open Graph Tags --&gt;
<meta property="og:title" content="Learn HTML5 - Complete Tutorial">
<meta property="og:description" content="Master HTML5 from basics to advanced techniques with hands-on examples.">
<meta property="og:image" content="https://example.com/images/og-preview.jpg">
<meta property="og:url" content="https://example.com/html-tutorial">
<meta property="og:type" content="website">
<meta property="og:site_name" content="WebDev Mastery">
<meta property="og:locale" content="en_US">

<!-- Optional but recommended --&gt;
<meta property="og:image:width" content="1200">
<meta property="og:image:height" content="630">
<meta property="og:image:alt" content="HTML5 Tutorial Preview Image">`;

    const twitterCardCode = `<!-- Summary Card with Large Image --&gt;
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:site" content="@YourTwitterHandle">
<meta name="twitter:creator" content="@AuthorTwitterHandle">
<meta name="twitter:title" content="Learn HTML5 - Complete Tutorial">
<meta name="twitter:description" content="Master HTML5 from basics to advanced techniques with hands-on examples.">
<meta name="twitter:image" content="https://example.com/images/twitter-preview.jpg">
<meta name="twitter:image:alt" content="HTML5 Tutorial Preview">`;

    const themeColorCode = `<meta name="theme-color" content="#1a73e8">
<meta name="theme-color" content="#ffffff" media="(prefers-color-scheme: light)">
<meta name="theme-color" content="#000000" media="(prefers-color-scheme: dark)">`;

    const referrerPolicyCode = `<meta name="referrer" content="strict-origin-when-cross-origin">`;

    const cspCode = `<meta http-equiv="Content-Security-Policy" content="default-src 'self'; script-src 'self' https://trusted-cdn.com; style-src 'self' 'unsafe-inline';">`;

    const formatDetectionCode = `<!-- Disable automatic phone number detection --&gt;
<meta name="format-detection" content="telephone=no">

<!-- Disable automatic email detection --&gt;
<meta name="format-detection" content="email=no">

<!-- Disable both --&gt;
<meta name="format-detection" content="telephone=no, email=no">`;

    const completeMetaExampleCode = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    
    <!-- Primary Meta Tags --&gt;
    <title>Learn HTML5 - Complete Tutorial | WebDev Mastery</title>
    <meta name="title" content="Learn HTML5 - Complete Tutorial | WebDev Mastery">
    <meta name="description" content="Master HTML5 from scratch with our comprehensive tutorial. Learn tags, semantics, forms, and modern web development practices.">
    <meta name="keywords" content="HTML tutorial, HTML5, web development, learn HTML, coding">
    <meta name="author" content="WebDev Mastery">
    <meta name="robots" content="index, follow">
    
    <!-- Canonical URL --&gt;
    <link rel="canonical" href="https://example.com/html-tutorial">
    
    <!-- Open Graph / Facebook --&gt;
    <meta property="og:type" content="website">
    <meta property="og:url" content="https://example.com/html-tutorial">
    <meta property="og:title" content="Learn HTML5 - Complete Tutorial">
    <meta property="og:description" content="Master HTML5 from basics to advanced techniques with hands-on examples.">
    <meta property="og:image" content="https://example.com/images/og-preview.jpg">
    <meta property="og:image:width" content="1200">
    <meta property="og:image:height" content="630">
    <meta property="og:site_name" content="WebDev Mastery">
    
    <!-- Twitter --&gt;
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:url" content="https://example.com/html-tutorial">
    <meta name="twitter:title" content="Learn HTML5 - Complete Tutorial">
    <meta name="twitter:description" content="Master HTML5 from basics to advanced techniques with hands-on examples.">
    <meta name="twitter:image" content="https://example.com/images/twitter-preview.jpg">
    <meta name="twitter:creator" content="@webdevmastery">
    
    <!-- Additional Meta Tags --&gt;
    <meta name="theme-color" content="#1a73e8">
    <meta name="referrer" content="strict-origin-when-cross-origin">
    <meta name="format-detection" content="telephone=no">
    
    <!-- Favicon --&gt;
    <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
    <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">
</head>
<body>
    <!-- Content --&gt;
</body>
</html>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Document Architecture</div>
                <h1>Meta Tags & SEO Optimization</h1>
                <p className="hero-lead">
                    Master the art of metadata. Learn how meta tags influence search rankings, social sharing,
                    and browser behavior for maximum visibility and performance.
                </p>
            </div>

            <div className="content-section">
                <h2>What are Meta Tags?</h2>
                <p>
                    Meta tags provide <strong>structured metadata</strong> about your HTML document. They don't appear
                    on the page but communicate essential information to browsers, search engines, and social media platforms.
                </p>

                <div className="syntax-box">
                    <h3>Meta Tag Syntax:</h3>
                    <CodeBlock language="html" code={metaTagSyntaxCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Critical SEO Meta Tags</h2>

                <div className="meta-tag-card">
                    <h3>1. Description Meta Tag</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={descriptionMetaCode} />
                    </div>

                    <ProTip><div className="info-icon"></div>
                        <div className="info-text">
                            <strong>Why It Matters:</strong> This text appears in search engine results below your title.
                            It's your "sales pitch" to convince users to click your link.
                        </div></ProTip>

                    <div className="best-practices">
                        <h4>Best Practices:</h4>
                        <ul>
                            <li>Keep it between 150-160 characters (search engines truncate longer descriptions)</li>
                            <li>Include your primary keyword naturally</li>
                            <li>Write compelling, action-oriented copy</li>
                            <li>Make it unique for each page</li>
                            <li> Don't stuff keywords</li>
                            <li> Don't duplicate descriptions across pages</li>
                        </ul>
                    </div>
                </div>

                <div className="meta-tag-card">
                    <h3>2. Robots Meta Tag</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={robotsMetaCode} />
                    </div>

                    <div className="directives-table">
                        <h4>Robot Directives:</h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>Directive</th>
                                    <th>Meaning</th>
                                    <th>Use Case</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>index</code></td>
                                    <td>Allow in search results</td>
                                    <td>Public content</td>
                                </tr>
                                <tr>
                                    <td><code>noindex</code></td>
                                    <td>Don't include in search results</td>
                                    <td>Duplicate/private pages</td>
                                </tr>
                                <tr>
                                    <td><code>follow</code></td>
                                    <td>Crawl links on this page</td>
                                    <td>Normal pages</td>
                                </tr>
                                <tr>
                                    <td><code>nofollow</code></td>
                                    <td>Don't crawl links</td>
                                    <td>User-generated content</td>
                                </tr>
                                <tr>
                                    <td><code>noarchive</code></td>
                                    <td>Don't cache this page</td>
                                    <td>Frequently updated content</td>
                                </tr>
                                <tr>
                                    <td><code>nosnippet</code></td>
                                    <td>Don't show text snippet in results</td>
                                    <td>Confidential content</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="meta-tag-card">
                    <h3>3. Canonical URL</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={canonicalUrlCode} />
                    </div>

                    <p>
                        Tells search engines which URL is the "authoritative" version when you have duplicate or similar content.
                    </p>

                    <div className="example-scenario">
                        <h4>Example Scenario:</h4>
                        <p>Your product is accessible at multiple URLs:</p>
                        <ul>
                            <li><code>example.com/products/widget</code></li>
                            <li><code>example.com/products/widget?color=blue</code></li>
                            <li><code>example.com/products/widget?ref=email</code></li>
                        </ul>
                        <p>All should have the same canonical tag pointing to the primary URL:</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={canonicalUrlExampleCode} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Open Graph Protocol (Social Media)</h2>
                <p>
                    Control how your content appears when shared on Facebook, LinkedIn, Discord, and other platforms.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Complete Open Graph Implementation</span>
                    </div>
                    <CodeBlock language="html" code={openGraphCode} />
                </div>

                <div className="og-specs">
                    <h3>Open Graph Image Specifications:</h3>
                    <ul>
                        <li><strong>Recommended size:</strong> 1200x630 pixels (1.91:1 ratio)</li>
                        <li><strong>Minimum size:</strong> 600x315 pixels</li>
                        <li><strong>Format:</strong> JPG, PNG, or WebP</li>
                        <li><strong>Max file size:</strong> 8 MB (keep under 1 MB for performance)</li>
                        <li><strong>Text on image:</strong> Keep minimal (may be cropped on mobile)</li>
                    </ul>
                </div>

                <div className="og-types">
                    <h3>Common <code>og:type</code> Values:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Use For</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>website</code></td>
                                <td>General website pages</td>
                            </tr>
                            <tr>
                                <td><code>article</code></td>
                                <td>Blog posts, news articles</td>
                            </tr>
                            <tr>
                                <td><code>video.other</code></td>
                                <td>Video content pages</td>
                            </tr>
                            <tr>
                                <td><code>product</code></td>
                                <td>E-commerce product pages</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Twitter Card Meta Tags</h2>
                <p>
                    Twitter uses its own meta tags (but falls back to Open Graph if Twitter tags are missing).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Twitter Card Setup</span>
                    </div>
                    <CodeBlock language="html" code={twitterCardCode} />
                </div>

                <div className="twitter-card-types">
                    <h3>Twitter Card Types:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Card Type</th>
                                <th>Description</th>
                                <th>Image Size</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>summary</code></td>
                                <td>Small square image</td>
                                <td>144x144 px (minimum)</td>
                            </tr>
                            <tr>
                                <td><code>summary_large_image</code></td>
                                <td>Large rectangular image (most common)</td>
                                <td>1200x628 px (recommended)</td>
                            </tr>
                            <tr>
                                <td><code>player</code></td>
                                <td>Video/audio player</td>
                                <td>Varies</td>
                            </tr>
                            <tr>
                                <td><code>app</code></td>
                                <td>Mobile app promotion</td>
                                <td>Varies</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Additional Important Meta Tags</h2>

                <div className="meta-tag-card">
                    <h3>Theme Color (Mobile Browsers)</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={themeColorCode} />
                    </div>
                    <p>Colors the browser's UI elements (address bar, status bar) on mobile devices.</p>
                </div>

                <div className="meta-tag-card">
                    <h3>Referrer Policy</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={referrerPolicyCode} />
                    </div>
                    <p>Controls how much referrer information is sent with requests.</p>
                    <div className="referrer-options">
                        <ul>
                            <li><code>no-referrer</code> - Never send referrer</li>
                            <li><code>origin</code> - Send only origin (domain)</li>
                            <li><code>strict-origin-when-cross-origin</code> - Full URL for same-origin, origin-only for cross-origin (recommended)</li>
                        </ul>
                    </div>
                </div>

                <div className="meta-tag-card">
                    <h3>Content Security Policy (CSP)</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={cspCode} />
                    </div>
                    <p>Helps prevent XSS attacks by restricting where resources can be loaded from.</p>
                    <Warning><strong> Note:</strong> CSP is better implemented via HTTP headers, but meta tags work as a fallback.</Warning>
                </div>

                <div className="meta-tag-card">
                    <h3>Format Detection (Mobile)</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={formatDetectionCode} />
                    </div>
                    <p>Prevents mobile browsers from automatically turning numbers into clickable phone links.</p>
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Your Meta Tags</h2>
                <p>
                    Always test how your meta tags render on different platforms:
                </p>

                <div className="tool-list">
                    <h3>Essential Testing Tools:</h3>
                    <ul>
                        <li>
                            <strong>Facebook Sharing Debugger:</strong>
                            <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer">
                                developers.facebook.com/tools/debug
                            </a>
                            <p>Test Open Graph tags and clear Facebook's cache</p>
                        </li>
                        <li>
                            <strong>Twitter Card Validator:</strong>
                            <a href="https://cards-dev.twitter.com/validator" target="_blank" rel="noopener noreferrer">
                                cards-dev.twitter.com/validator
                            </a>
                            <p>Preview how your Twitter cards will appear</p>
                        </li>
                        <li>
                            <strong>LinkedIn Post Inspector:</strong>
                            <a href="https://www.linkedin.com/post-inspector/" target="_blank" rel="noopener noreferrer">
                                linkedin.com/post-inspector
                            </a>
                            <p>Test LinkedIn sharing and clear their cache</p>
                        </li>
                        <li>
                            <strong>Google Rich Results Test:</strong>
                            <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer">
                                search.google.com/test/rich-results
                            </a>
                            <p>Check structured data and SEO meta tags</p>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Meta Tags Example</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Production-Ready Meta Tags</span>
                    </div>
                    <CodeBlock language="html" code={completeMetaExampleCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Checklist</h2>
                <div className="checklist">
                    <h3>SEO Checklist:</h3>
                    <ul>
                        <li> Unique, compelling title (50-60 characters)</li>
                        <li> Descriptive meta description (150-160 characters)</li>
                        <li> Canonical URL set correctly</li>
                        <li> Robots meta tag configured appropriately</li>
                        <li> Author and keywords included</li>
                    </ul>

                    <h3>Social Media Checklist:</h3>
                    <ul>
                        <li> Open Graph tags complete (title, description, image, URL)</li>
                        <li> Twitter Card tags configured</li>
                        <li> OG image meets size requirements (1200x630)</li>
                        <li> Tested in Facebook/Twitter/LinkedIn debuggers</li>
                    </ul>

                    <h3>Technical Checklist:</h3>
                    <ul>
                        <li> Character encoding declared</li>
                        <li> Viewport meta tag for responsive design</li>
                        <li> Theme color for mobile browsers</li>
                        <li> Referrer policy configured</li>
                        <li> All meta tags before any <code>&lt;link&gt;</code> or <code>&lt;script&gt;</code> tags</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLMetadata;


